import { css } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import type { DegreeListsV3 } from 'bundles/browse-common/legacy/types/degree';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import BrowseDegreeCard, {
  BrowseDegreeCardBare,
} from 'bundles/unified-seo-content-common/components/browse/BrowseDegreeCard';
import Collection from 'bundles/unified-seo-content-common/components/browse/Collection';
import CollectionPlaceholder from 'bundles/unified-seo-content-common/components/browse/CollectionPlaceholder';
import {
  DEGREE_CARD_HEIGHT,
  PADDING_CHANGE_BREAKPOINT,
  condensedResponsiveProperty,
  getDegreeCollectionLabel,
} from 'bundles/unified-seo-content-common/components/browse/constants';
import { withMediaMaxWidthScreenTablet } from 'bundles/unified-seo-content-common/components/browse/utils';

declare global {
  interface Window {
    appName?: string;
  }
}

export type DegreeListsV3WithMetadata = DegreeListsV3 & {
  metadata?: { header?: React.ReactNode; footer?: React.ReactNode };
};

export type CarouselCollection = DegreeListsV3WithMetadata | null;

type InputProps = {
  collections: CarouselCollection[];
  shouldUseBareComponent?: boolean;
  isDiscoveryCollectionsLoading?: boolean;
  shouldUseCondensedView?: boolean;
};

type Props = InputProps & {
  userAgent?: UserAgentInfo;
  appName?: string;
  matches?: boolean;
};

export const condensedStyles = {
  browseCarousel: css`
    @media (max-width: ${PADDING_CHANGE_BREAKPOINT}px) {
      padding: 30px 0 !important;
    }

    .rc-BrowseCarousel {
      .slick-slide {
        min-width: 254px !important;

        & > div {
          display: flex;
          justify-content: center;
        }

        .rc-BrowseDegreeCard {
          width: 242px;
        }
      }

      .slick-prev {
        left: -55px !important;
      }

      .slick-next {
        right: -48px !important;
      }
    }

    .rc-BrowseCarousel > .slick-slider > ul.carousel-dots {
      display: block !important;

      @media (max-width: ${PADDING_CHANGE_BREAKPOINT}px) {
        margin-top: 0;
      }
    }

    .slick-list {
      margin-right: 0 !important;
    }
  `,
  placeholderCards: css`
    padding: 50px 0 !important;

    @media (max-width: ${PADDING_CHANGE_BREAKPOINT}px) {
      padding: 20px 0 !important;
    }

    .rc-BrowseProductCard {
      width: 236px !important;
      min-width: unset !important;
      height: 300px !important;

      & > div {
        height: 300px !important;
      }
    }
  `,
  loadingCarousel: css`
    .placeholder {
      .slick-track {
        display: flex;

        .slick-slide {
          width: 254px !important;
        }
      }
    }
  `,
};
export class CollectionsSection extends React.PureComponent<Props> {
  renderDegreeCollections = (collectionItem: DegreeListsV3WithMetadata, index: number) => {
    const { shouldUseBareComponent, shouldUseCondensedView } = this.props;

    // Rendering a carousel without any items breaks its initial internal state, so don't do that.
    if (!collectionItem?.entries || collectionItem.entries.length === 0) {
      return null;
    }

    const collectionLabel = collectionItem.collectionName?.() || getDegreeCollectionLabel();

    return (
      <div key={collectionLabel} id={collectionLabel} className="rc-CollectionsSection">
        {/** using key and Id above to fix promo units weird rendering */}
        <Collection
          key={collectionLabel}
          // using key here to fix UI issue rerendering the collections
          CollectionItem={shouldUseBareComponent ? BrowseDegreeCardBare : BrowseDegreeCard}
          collectionItemHeight={DEGREE_CARD_HEIGHT}
          header={collectionItem.metadata?.header}
          footer={collectionItem.metadata?.footer}
          entities={collectionItem.entries}
          collectionName={collectionLabel}
          trackingData={{ label: collectionLabel, id: 'degrees', collectionIndex: index }}
          data-e2e="degree-card-collection"
          shouldUseBareComponent={shouldUseBareComponent}
          isDegreeOrMastertrackCollection
          styleAdditions={shouldUseCondensedView ? condensedStyles.browseCarousel : undefined}
          responsive={shouldUseCondensedView ? condensedResponsiveProperty : undefined}
          slideLength={shouldUseCondensedView ? 3 : 4}
        />
      </div>
    );
  };

  renderCollections = () => {
    const { collections } = this.props;

    if (!collections || collections.length === 0) return null;

    const collectionsComponent = collections.map((collectionItem: CarouselCollection, index: number) => {
      return this.renderDegreeCollections(collectionItem as DegreeListsV3, index);
    });

    return collectionsComponent;
  };

  render() {
    const { isDiscoveryCollectionsLoading, shouldUseCondensedView } = this.props;
    return (
      <div>
        {!isDiscoveryCollectionsLoading ? (
          this.renderCollections()
        ) : (
          <CollectionPlaceholder
            styleAdditions={
              shouldUseCondensedView ? [condensedStyles.browseCarousel, condensedStyles.placeholderCards] : undefined
            }
            numberOfPlaceholders={shouldUseCondensedView ? 3 : undefined}
          />
        )}
      </div>
    );
  }
}

export default compose<Props, InputProps>(
  connectToStores<Props, InputProps>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    userAgent: ApplicationStore.getUserAgent(),
    appName: ApplicationStore.appName,
  })),
  withMediaMaxWidthScreenTablet
)(CollectionsSection);
